import { formatNumber } from "./format";
import type { MessageParams } from "./runtime";

// Presentation of server errors (LOC-707, decision E-1). One resolver turns an
// HTTP error (status, parsed legacy body with an optional additive `l10n`
// member) into a trusted catalog key and typed params. Keys come only from the
// build-time registry of this client or from the caller; no value of the
// response ever becomes a key or is rendered, and nothing here decides a retry,
// a redirect or any other branch: callers keep branching on status and legacy
// codes exactly as before.

export type ServerErrorParamSpec =
  | { readonly type: "integer"; readonly min: number; readonly max: number; readonly field?: string }
  | { readonly type: "number"; readonly min: number; readonly max: number; readonly fraction_digits: number; readonly field?: string }
  | { readonly type: "enum"; readonly values: readonly string[]; readonly field?: string };

export interface ServerErrorMessageSpec {
  readonly key: string;
  readonly params?: Readonly<Record<string, ServerErrorParamSpec>>;
  /** Param-free key used when the params are missing or invalid; present whenever params are. */
  readonly fallback_key?: string;
}

export type ServerErrorCodeField = "error" | "code" | "error_code" | "reason" | "status";

export interface ServerErrorFamily {
  /** Legacy body fields holding the code, in reading order. */
  readonly fields: readonly ServerErrorCodeField[];
  readonly codes: Readonly<Record<string, ServerErrorMessageSpec>>;
}

/** This client's projection of localization/errors/registry.json (`@voltie/localization-web/server-errors`). */
export interface ServerErrorRegistry {
  readonly schema_version: 1;
  readonly client_id: string;
  readonly generic: { readonly network: string; readonly unauthorized: string; readonly forbidden: string; readonly rate_limited: string };
  readonly ids: Readonly<Record<string, ServerErrorMessageSpec>>;
  readonly families: Readonly<Record<string, ServerErrorFamily>>;
}

export interface ServerErrorInput {
  /** HTTP status of the response; null or absent when there was none. */
  readonly status?: number | null;
  /** Parsed JSON body; anything that is not a plain JSON record (own members, plain or null prototype) is ignored. */
  readonly body?: unknown;
  /** True when the request got no HTTP response (offline, dropped connection, timeout). */
  readonly network?: boolean;
}

export interface ServerErrorContext {
  /** The screen's own message for this failure ("Couldn't save the group"); a literal key of the caller. */
  readonly fallback: string;
  /** Registry family of the endpoint, whose existing codes take precedence over `l10n`. */
  readonly family?: string;
  /** Screen-specific keys for statuses (for example 401 on a sign-in form); literal keys of the caller. */
  readonly statusKeys?: Readonly<Partial<Record<number, string>>>;
}

/** Which rule decided; for tests and diagnostics, never for business branches. */
export type ServerErrorSource = "family" | "family_fallback" | "l10n" | "l10n_fallback" | "network" | "status" | "context";

export interface ResolvedServerError {
  readonly key: string;
  readonly params?: MessageParams;
  readonly source: ServerErrorSource;
}

/**
 * Why a server-provided presentation was not used as sent. Fixed reasons only:
 * the id, the params and the body are never part of the event.
 */
export type ServerErrorFallbackReason = "invalid_envelope" | "unknown_id" | "invalid_params" | "invalid_code_params";

export type ServerErrorEvent = { readonly type: "server_error_fallback"; readonly reason: ServerErrorFallbackReason };

export interface ServerErrorResolver {
  resolve(input: ServerErrorInput, context: ServerErrorContext): ResolvedServerError;
  /** `t` of the resolved key and params. */
  message(t: (key: string, params?: MessageParams) => string, input: ServerErrorInput, context: ServerErrorContext): string;
}

export interface ServerErrorResolverOptions {
  readonly registry: ServerErrorRegistry;
  /** Observer of fallbacks, for example the app's localization metrics `onEvent`. A throwing observer is ignored. */
  readonly onEvent?: (event: ServerErrorEvent) => void;
}

const ERROR_ID = /^[a-z][a-z0-9_]{0,63}$/;

type PlainObject = Readonly<Record<string, unknown>>;

/**
 * A record as JSON.parse makes it: its prototype is `Object.prototype` (of any realm) or null.
 * Arrays, Dates, class instances and objects created from another object are not envelopes.
 */
function isPlainObject(value: unknown): value is PlainObject {
  if (typeof value !== "object" || value === null) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === null || Object.getPrototypeOf(proto) === null;
}

/** Own member of a registry map or a response record; inherited names such as `constructor` are never read. */
function own<T>(record: Readonly<Record<string, T>>, name: string): T | undefined {
  return Object.hasOwn(record, name) ? record[name] : undefined;
}

/** One validated and formatted param value, or undefined when it does not satisfy its spec. */
function paramValue(spec: ServerErrorParamSpec, value: unknown): string | number | undefined {
  switch (spec.type) {
    case "integer":
      return typeof value === "number" && Number.isInteger(value) && value >= spec.min && value <= spec.max ? value : undefined;
    case "number":
      return typeof value === "number" && Number.isFinite(value) && value >= spec.min && value <= spec.max ? formatNumber(value, spec.fraction_digits) : undefined;
    case "enum":
      return typeof value === "string" && spec.values.includes(value) ? value : undefined;
    default:
      return undefined;
  }
}

/**
 * The params of `spec` read from `source`, all or nothing: a missing, extra
 * (when `exact`), mistyped or out-of-bounds value gives null, never partial params.
 */
function readParams(spec: ServerErrorMessageSpec, source: PlainObject, exact: boolean, field: (name: string, s: ServerErrorParamSpec) => string): MessageParams | null {
  const specs = spec.params ?? {};
  const names = Object.keys(specs);
  if (exact && Object.keys(source).some((name) => !Object.hasOwn(specs, name))) return null;
  const out: Record<string, string | number> = {};
  for (const name of names) {
    const at = field(name, specs[name]);
    if (!Object.hasOwn(source, at)) return null;
    const value = paramValue(specs[name], source[at]);
    if (value === undefined) return null;
    out[name] = value;
  }
  return out;
}

const resolved = (key: string, source: ServerErrorSource, params?: MessageParams | null): ResolvedServerError =>
  params && Object.keys(params).length ? { key, params, source } : { key, source };

export function createServerErrorResolver(options: ServerErrorResolverOptions): ServerErrorResolver {
  const { registry } = options;
  const report = (reason: ServerErrorFallbackReason) => {
    try {
      options.onEvent?.({ type: "server_error_fallback", reason });
    } catch {
      // Diagnostics must not break the error display.
    }
  };

  function fromFamily(body: PlainObject, name: string | undefined): ResolvedServerError | null {
    const family = name === undefined ? undefined : own(registry.families, name);
    if (!family) return null;
    for (const field of family.fields) {
      const code = own(body, field);
      if (typeof code !== "string") continue;
      const spec = own(family.codes, code);
      if (!spec) return null;
      if (!spec.params) return resolved(spec.key, "family");
      const params = readParams(spec, body, false, (param, s) => s.field ?? param);
      if (params) return resolved(spec.key, "family", params);
      report("invalid_code_params");
      return resolved(spec.fallback_key ?? spec.key, "family_fallback");
    }
    return null;
  }

  function fromL10n(body: PlainObject): ResolvedServerError | null {
    if (!Object.hasOwn(body, "l10n")) return null;
    const l10n = body.l10n;
    const id = isPlainObject(l10n) ? own(l10n, "id") : undefined;
    if (!isPlainObject(l10n) || typeof id !== "string" || !ERROR_ID.test(id)) {
      report("invalid_envelope");
      return null;
    }
    const spec = own(registry.ids, id);
    if (!spec) {
      report("unknown_id");
      return null;
    }
    const extraMember = Object.keys(l10n).some((name) => name !== "id" && name !== "params");
    const rawParams = Object.hasOwn(l10n, "params") ? l10n.params : {};
    const params = !extraMember && isPlainObject(rawParams) ? readParams(spec, rawParams, true, (param) => param) : null;
    if (params) return resolved(spec.key, "l10n", params);
    report("invalid_params");
    return resolved(spec.fallback_key ?? spec.key, "l10n_fallback");
  }

  function fromStatus(status: number | null, context: ServerErrorContext): ResolvedServerError {
    if (status !== null) {
      const screenKey = context.statusKeys ? own(context.statusKeys, String(status)) : undefined;
      if (screenKey) return resolved(screenKey, "status");
      if (status === 401) return resolved(registry.generic.unauthorized, "status");
      if (status === 403) return resolved(registry.generic.forbidden, "status");
      if (status === 429) return resolved(registry.generic.rate_limited, "status");
    }
    return resolved(context.fallback, "context");
  }

  function resolve(input: ServerErrorInput, context: ServerErrorContext): ResolvedServerError {
    const status = typeof input.status === "number" && Number.isInteger(input.status) && input.status >= 100 && input.status <= 599 ? input.status : null;
    if (status === null && input.network === true) return resolved(registry.generic.network, "network");
    const body = isPlainObject(input.body) ? input.body : null;
    if (body) {
      const decided = fromFamily(body, context.family) ?? fromL10n(body);
      if (decided) return decided;
    }
    return fromStatus(status, context);
  }

  return {
    resolve,
    message(t, input, context) {
      const { key, params } = resolve(input, context);
      return t(key, params);
    },
  };
}
